import { useMatches, type UIMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { BreadcrumbItemData } from './Breadcrumbs';

/**
 * W16-F4-S2 — Route-metadata-driven breadcrumb hook.
 *
 * Reads `useMatches()` and projects each match's `handle.breadcrumb`
 * into a `BreadcrumbItemData[]`.
 *
 * Routes opt in by typing their `handle` field as `BreadcrumbHandle`:
 *
 *   { path: '/buildings', handle: { breadcrumb: 'nav.buildings' } satisfies BreadcrumbHandle }
 *
 * The `breadcrumb` field can be either:
 *   - a literal string (an i18n key — `useBreadcrumbs` resolves via `t(...)`),
 *   - a function `(match, t) => string` for routes whose label depends on
 *     params or already-resolved data passed via the match.
 *
 * Resolution rules:
 *   - The last surviving item drops its `to` (current page, no link).
 *   - Function-based labels that throw (e.g. data not yet loaded) are
 *     skipped silently; the trail keeps rendering what it can.
 *   - Per the issue spec, this hook is synchronous and does not fetch.
 *     Detail pages with dynamic names use react-query inside the page
 *     and the hook surfaces the fallback (e.g. "Building #123") until
 *     the page-level `Breadcrumbs` consumer overrides via context
 *     (W16-F4-S3).
 */

export type BreadcrumbResolver = (
  match: UIMatch,
  t: TFunction,
) => string | null | undefined;

export interface BreadcrumbHandle {
  breadcrumb: string | BreadcrumbResolver;
  /**
   * When `false`, the rendered item has no `to` even if it isn't the
   * last match — useful for synthetic "category" rows. Defaults to
   * `true` (linkable when not the last item).
   */
  link?: boolean;
}

function isHandleWithBreadcrumb(
  handle: unknown,
): handle is BreadcrumbHandle {
  return (
    typeof handle === 'object' &&
    handle !== null &&
    'breadcrumb' in (handle as Record<string, unknown>) &&
    (typeof (handle as { breadcrumb: unknown }).breadcrumb === 'string' ||
      typeof (handle as { breadcrumb: unknown }).breadcrumb === 'function')
  );
}

export function useBreadcrumbs(): BreadcrumbItemData[] {
  const matches = useMatches();
  const { t } = useTranslation();
  const items: BreadcrumbItemData[] = [];

  for (const match of matches) {
    if (!isHandleWithBreadcrumb(match.handle)) continue;
    const handle = match.handle;
    let label: string | null | undefined;
    try {
      if (typeof handle.breadcrumb === 'function') {
        label = handle.breadcrumb(match, t);
      } else {
        // String → treat as i18n key; fall back to the key itself when
        // missing so the trail never silently disappears.
        label = t(handle.breadcrumb, { defaultValue: handle.breadcrumb });
      }
    } catch {
      continue;
    }
    if (label == null || label === '') continue;
    items.push({
      label,
      to: handle.link === false ? undefined : match.pathname,
      path: match.pathname,
    });
  }

  if (items.length > 0) {
    items[items.length - 1] = { ...items[items.length - 1]!, to: undefined };
  }

  return items;
}
